// src/services/courses.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../app/store";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE || "http://localhost:4000/api",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    headers.set("content-type", "application/json");
    return headers;
  },
});

export const coursesApi = createApi({
  reducerPath: "coursesApi",
  baseQuery,
  tagTypes: ["Courses", "Modules", "Lessons"],
  endpoints: (builder) => ({
    // Legacy: basic public get (published only by default)
    getCourses: builder.query<any[], { published?: boolean } | void>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params && params.published !== undefined) {
          queryParams.published = String(params.published);
        }
        const qs = new URLSearchParams(queryParams).toString();
        return `/courses${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Courses"],
    }),

    // New: admin-only listing (requires admin token)
    getAdminCourses: builder.query<any[], void>({
      query: () => ({
        url: `/courses/admin-get`,
        method: "GET",
      }),
      providesTags: ["Courses"],
    }),

    // New: student/public listing (published only)
    getStudentCourses: builder.query<any[], { published?: boolean } | void>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params && params.published !== undefined) {
          queryParams.published = String(params.published);
        } else {
          // default published=true
          queryParams.published = "true";
        }
        const qs = new URLSearchParams(queryParams).toString();
        return {
          url: `/courses/student-get${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["Courses"],
    }),

    // Get single course by ID (existing)
    getCourse: builder.query({
      query: (id: string) => ({
        url: `/courses/get-one-course/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Courses", id }],
    }),

    // Course admin mutations (unchanged)
    createCourse: builder.mutation({
      query: (body: {
        title: string;
        description?: string;
        published?: boolean;
      }) => ({
        url: "/courses",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Courses"],
    }),

    updateCourse: builder.mutation({
      query: ({
        id,
        ...body
      }: {
        id: string;
        title?: string;
        description?: string;
        published?: boolean;
      }) => ({
        url: `/courses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Courses", id },
        "Courses",
      ],
    }),

    deleteCourse: builder.mutation({
      query: (id: string) => ({ url: `/courses/${id}`, method: "DELETE" }),
      invalidatesTags: ["Courses"],
    }),

    publishCourse: builder.mutation({
      query: (id: string) => ({
        url: `/courses/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Courses", id },
        "Courses",
      ],
    }),

    unpublishCourse: builder.mutation({
      query: (id: string) => ({
        url: `/courses/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Courses", id },
        "Courses",
      ],
    }),

    // Modules & lessons (unchanged)
    addModule: builder.mutation({
      query: ({
        courseId,
        ...body
      }: {
        courseId: string;
        title: string;
        description?: string;
        order?: number;
      }) => ({
        url: `/courses/${courseId}/modules`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "Courses", id: courseId },
        "Modules",
      ],
    }),

    updateModule: builder.mutation({
      query: ({
        courseId,
        moduleId,
        ...body
      }: {
        courseId: string;
        moduleId: string;
        title?: string;
        description?: string;
        order?: number;
      }) => ({
        url: `/courses/${courseId}/modules/${moduleId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "Courses", id: courseId },
        "Modules",
      ],
    }),

    deleteModule: builder.mutation({
      query: ({
        courseId,
        moduleId,
      }: {
        courseId: string;
        moduleId: string;
      }) => ({
        url: `/courses/${courseId}/modules/${moduleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "Courses", id: courseId },
        "Modules",
      ],
    }),

    addLesson: builder.mutation({
      query: (formData: FormData) => ({
        url: `/courses/${formData.get("courseId")}/modules/${formData.get(
          "moduleId",
        )}/lessons`,
        method: "POST",
        body: formData,
        // Important: Don't set Content-Type header
        // Browser will automatically set it as multipart/form-data with boundary
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Courses" }, "Lessons"],
    }),

    updateLesson: builder.mutation({
      query: ({ courseId, moduleId, lessonId, ...formData }) => {
        const data = new FormData();
        Object.keys(formData).forEach((key) => {
          if (formData[key] !== undefined && formData[key] !== null) {
            data.append(key, formData[key]);
          }
        });

        return {
          url: `/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Courses", id: arg.courseId },
        "Lessons",
      ],
    }),

    deleteLesson: builder.mutation({
      query: ({ courseId, moduleId, lessonId }: any) => ({
        url: `/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "Courses", id: courseId },
        "Lessons",
      ],
    }),
  }),
});

export const {
  useGetCoursesQuery,
  useGetCourseQuery,

  // new hooks
  useGetAdminCoursesQuery,
  useGetStudentCoursesQuery,

  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  usePublishCourseMutation,
  useUnpublishCourseMutation,

  useAddModuleMutation,
  useUpdateModuleMutation,
  useDeleteModuleMutation,

  useAddLessonMutation,
  useUpdateLessonMutation,
  useDeleteLessonMutation,
} = coursesApi;

export default coursesApi;
